"use client";

import React, { useState } from "react";
import { useQuery } from "@apollo/client/react";
import RouterLink from "next/link";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import {
	CircleHelp,
	CircleUserRoundIcon,
	LogOut,
	Mail,
	Plus,
	Settings,
	User,
} from "lucide-react";

import {
	cn,
	textVariants,
	Breakpoint,
	Link,
	LinkVariant,
	useValueAtBreakpoint,
} from "@jasperlabs/ui";

import { queries } from "@/api";
import { useLogOut } from "@/contexts/logOut";
import { CARUSO_HELP_CENTER } from "@/constants/external-links";
import { TenantSupportLink } from "@/tenants/components";

import { formatFullName } from "@/components/utilities/stringFormat";

const LINK_STYLES =
	"bg-white py-2 px-5 transition-colors duration-200 hover:bg-neutral-50  flex w-full focus:no-underline";

const AccountDropdownMenu = () => {
	const [isOpen, setIsOpen] = useState(false);

	const { logOut } = useLogOut();

	const { data = {} } = useQuery(queries.ME);
	const { account = null } = data;
	const { firstName, lastName, email } = account || {};
	const fullName = formatFullName({ firstName, lastName });

	const menuButtonAriaLabel = isOpen
		? "Close account menu"
		: "Open account menu";

	const sideOffset = useValueAtBreakpoint({
		[Breakpoint.XS]: 15,
		[Breakpoint.LG]: 12,
	});

	return (
		<DropdownMenu.Root onOpenChange={(open) => setIsOpen(open)}>
			<DropdownMenu.Trigger asChild>
				<button
					type="button"
					className="text-white hover:bg-neutral-100/20 flex h-[40px] items-center gap-2 rounded-md bg-transparent px-2 py-2 text-[30px] lg:h-[48px]"
					aria-label={menuButtonAriaLabel}
				>
					<CircleUserRoundIcon className="size-[25px]" />
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content
					aria-label="Account Menu"
					className="shadow-card-sm z-[9000] ml-[1vw] w-[98vw] divide-y divide-neutral-200 overflow-hidden rounded-md bg-white md:ml-0 md:w-[250px] origin-[var(--radix-dropdown-menu-content-transform-origin)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
					sideOffset={sideOffset}
					align="end"
					alignOffset={0}
				>
					<div className="flex py-3 px-5">
						{account && (
							<div className="w-full">
								<div
									className={cn(
										textVariants({ variant: "subheading-sm" }),
										"truncate text-neutral-800",
									)}
									aria-label={fullName}
								>
									{fullName}
								</div>
								<div
									className={cn(
										textVariants({ variant: "body-sm" }),
										"truncate text-neutral-500",
									)}
								>
									User ID: {account?.id}
								</div>
								<div
									className={cn(
										textVariants({ variant: "body-sm" }),
										"truncate text-neutral-500",
									)}
									aria-label={email}
								>
									{email}
								</div>
							</div>
						)}
					</div>
					<section>
						<DropdownMenu.DropdownMenuItem asChild>
							<Link
								icon={<User className="size-[18px]" />}
								variant={LinkVariant.Black}
								className={cn(
									textVariants({ variant: "body-default" }),
									LINK_STYLES,
								)}
								href="/preferences"
								as={RouterLink}
							>
								Account preferences
							</Link>
						</DropdownMenu.DropdownMenuItem>
						<DropdownMenu.DropdownMenuItem asChild>
							<Link
								icon={<Settings className="size-[18px]" />}
								variant={LinkVariant.Black}
								className={cn(
									textVariants({ variant: "body-default" }),
									LINK_STYLES,
								)}
								href="/investing-entities/details"
								as={RouterLink}
							>
								Investing entity settings
							</Link>
						</DropdownMenu.DropdownMenuItem>
						<DropdownMenu.DropdownMenuItem asChild>
							<Link
								icon={<Plus className="size-[18px]" />}
								variant={LinkVariant.Black}
								className={cn(
									textVariants({ variant: "body-default" }),
									LINK_STYLES,
								)}
								href="/add-investing-entity"
								as={RouterLink}
							>
								Add investing entity
							</Link>
						</DropdownMenu.DropdownMenuItem>
						<DropdownMenu.DropdownMenuItem asChild>
							<TenantSupportLink
								icon={<Mail className="size-[18px]" />}
								className={cn(
									textVariants({ variant: "body-default" }),
									"text-neutral-800",
									LINK_STYLES,
								)}
							>
								Contact us
							</TenantSupportLink>
						</DropdownMenu.DropdownMenuItem>
						<DropdownMenu.DropdownMenuItem asChild>
							<Link
								icon={<CircleHelp className="size-[20px]" />}
								variant={LinkVariant.Black}
								className={cn(
									textVariants({ variant: "body-default" }),
									LINK_STYLES,
								)}
								href={CARUSO_HELP_CENTER}
								target="_blank"
							>
								Help
							</Link>
						</DropdownMenu.DropdownMenuItem>
						<DropdownMenu.DropdownMenuItem asChild>
							<Link
								icon={<LogOut className="size-[18px]" />}
								variant={LinkVariant.Black}
								className={cn(
									textVariants({ variant: "body-default" }),
									LINK_STYLES,
								)}
								as="button"
								onClick={logOut}
							>
								Log out
							</Link>
						</DropdownMenu.DropdownMenuItem>
					</section>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
};

export default AccountDropdownMenu;
